import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import axiosClient from '../api/axiosClient';
import type { Course } from '../types';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Courses() {
  const { user, isLoading } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Ref for file input
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);

  // Fetch courses (original code untouched)
  const fetchCourses = async () => {
    try {
      const response = await axiosClient.get('/elearning/api/courses/', {
        headers: { 'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '' },
      });
      const coursesData = Array.isArray(response.data) ? response.data : [];
      let filteredCourses: Course[] = [];

      if (user?.role === 'teacher') {
        filteredCourses = coursesData
          .filter((course: any) => course?.teacher && Number(course.teacher.id) === user.id)
          .map((course: any) => ({
            id: Number(course.id),
            name: course.name || '',
            description: course.description || '',
            enrolled: false,
            slug: course.slug || `course-${course.id}`,
            teacher: {
              id: Number(course.teacher.id),
              username: course.teacher.username || '',
              real_name: course.teacher.real_name || null,
              email: course.teacher.email || null,
              photo: course.teacher.photo || null,
              role: course.teacher.role || 'teacher',
            },
            materials: course.materials || null,
          }));
      } else if (user?.role === 'student') {
        let enrolledCourseIds: number[] = [];
        try {
          const enrollmentsResponse = await axiosClient.get('/elearning/api/enrollments/', {
            headers: { 'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '' },
          });
          enrolledCourseIds = Array.isArray(enrollmentsResponse.data)
            ? enrollmentsResponse.data
                .filter((e: any) => e.course && (typeof e.course.id === 'number' || typeof e.course.id === 'string'))
                .map((e: any) => Number(e.course.id))
            : [];
        } catch {}
        filteredCourses = coursesData
          .filter((course: any) => course && course.id && course.teacher)
          .map((course: any) => ({
            id: Number(course.id),
            name: course.name || '',
            description: course.description || '',
            enrolled: enrolledCourseIds.includes(Number(course.id)),
            slug: course.slug || `course-${course.id}`,
            teacher: {
              id: Number(course.teacher.id),
              username: course.teacher.username || '',
              real_name: course.teacher.real_name || null,
              email: course.teacher.email || null,
              photo: course.teacher.photo || null,
              role: course.teacher.role || 'teacher',
            },
            materials: course.materials || null,
          }));
      }

      setCourses(filteredCourses);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error
        ? JSON.stringify(err.response.data.error, null, 2)
        : err.message || 'Failed to fetch courses';
      console.error('[Courses] Error fetching courses=', errorMessage);
      setError(errorMessage);
      toast.error(`Error fetching courses: ${errorMessage}`);
    }
  };

  useEffect(() => {
    if (!isLoading && !user) navigate('/login');
    else if (user) fetchCourses();
  }, [isLoading, user, navigate]);

  // Enroll/Unenroll/Delete functions (original code untouched)
  const handleEnroll = async (courseId: number) => {
    try {
      const response = await axiosClient.post(`/elearning/api/courses/${courseId}/enroll/`,
          {},
          );
      if (response.status === 201 || response.status === 200) {
        await fetchCourses();
        setError(null);
        toast.success('Successfully enrolled in the course!');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail
        ? JSON.stringify(err.response.data.detail, null, 2)
        : err.message || 'Failed to enroll';
      console.error('[Courses] Error enrolling=', errorMessage);
      if (err.response?.status === 500) {
        await fetchCourses();
        setError('Enrollment may have succeeded. If not, please try again.');
        toast.warn('Enrollment may have succeeded. Refresh your courses.');
      } else {
        setError(errorMessage);
        toast.error(`Failed to enroll: ${errorMessage}`);
      }
    }
  };

  const handleUnenroll = async (courseId: number) => {
    try {
      await axiosClient.post(`/elearning/api/courses/${courseId}/unenroll/`, {});
      setCourses(courses.map(course => course.id === courseId ? { ...course, enrolled: false } : course));
      setError(null);
      toast.info('You have unenrolled from the course.');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail
        ? JSON.stringify(err.response.data.detail, null, 2)
        : err.message || 'Failed to unenroll';
      console.error('[Courses] Error unenrolling=', errorMessage);
      setError(errorMessage);
      toast.error(`Failed to unenroll: ${errorMessage}`);
    }
  };

  const handleDelete = async (courseId: number) => {
    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) return;
    try {
      await axiosClient.delete(`/elearning/api/courses/${courseId}/`);
      setCourses(courses.filter(course => course.id !== courseId));
      setError(null);
      toast.success('Course deleted successfully.');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail
        ? JSON.stringify(err.response.data.detail, null, 2)
        : err.message || 'Failed to delete course';
      setError(errorMessage);
      toast.error(`Failed to delete course: ${errorMessage}`);
    }
  };

  // ===== New Material Upload functionality =====
  const handleAddMaterialClick = (courseId: number) => {
    setSelectedCourseId(courseId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !selectedCourseId) return;
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('materials', file);

    try {
      const csrfResponse = await axiosClient.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = csrfResponse.data.csrfToken;

      await axiosClient.post(`/elearning/api/courses/${selectedCourseId}/materials/`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data', 'X-CSRFToken': csrfToken },
      });

      toast.success('Material uploaded successfully!');
      await fetchCourses();
    } catch (err: any) {
      toast.error(`Failed to upload material: ${err.message}`);
    } finally {
      e.target.value = '';
    }
  };

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
    </div>
  );

  if (!user) return null;

  return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Page Title */}
      <h1 className="text-4xl font-extrabold text-gray-900 mb-10 tracking-tight">
        📚 Courses
      </h1>

      {/* Navigation buttons */}
      <div className="mb-10 flex flex-wrap gap-4">
        <button
          onClick={() => navigate(-1)}
          className="px-5 py-2.5 bg-blue-600 text-white font-medium rounded-xl shadow hover:bg-blue-700 transition"
        >
          ⬅ Back
        </button>
        <button
          onClick={() => navigate('/')}
          className="px-5 py-2.5 bg-blue-600 text-white font-medium rounded-xl shadow hover:bg-blue-700 transition"
        >
          🏠 Homepage
        </button>
        {user.role === 'teacher' && (
          <a
            href="/courses/create"
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-xl shadow hover:from-purple-700 hover:to-indigo-700 transition"
          >
            ➕ Create New Course
          </a>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-8 p-4 bg-red-100 text-red-800 rounded-xl shadow flex items-start">
          <svg
            className="h-5 w-5 mr-2 mt-0.5 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <pre className="whitespace-pre-wrap">{error}</pre>
        </div>
      )}

      {/* Courses Grid */}
      {courses.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.map(course => (
            <div
              key={course.id}
              className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition p-6 flex flex-col justify-between border border-gray-100"
            >
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {course.name}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {course.description}
                </p>
                <p className="text-gray-700 mb-4 font-medium">
                  👨‍🏫 Instructor:{' '}
                  {course.teacher.real_name || course.teacher.username}
                </p>

                {/* Enrolled/Status Tag */}
                {user.role === 'student' && (
                  <span
                    className={`inline-block px-3 py-1 text-sm font-semibold rounded-full mb-4 ${
                      course.enrolled
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {course.enrolled ? '✅ Enrolled' : 'Not Enrolled'}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mt-4">
                <a
                  href={`/courses/${course.id}`}
                  className="flex-1 text-center px-4 py-2 bg-blue-600 text-white rounded-xl shadow hover:bg-blue-700 transition"
                >
                  🔎 View
                </a>

                {(course.enrolled ||
                  (user.role === 'teacher' && course.teacher.id === user.id)) && (
                  <a
                    href={`/chat/${course.slug}`}
                    className="flex-1 text-center px-4 py-2 bg-green-600 text-white rounded-xl shadow hover:bg-green-700 transition"
                  >
                    💬 Chat
                  </a>
                )}

                {user.role === 'student' &&
                  (course.enrolled ? (
                    <button
                      onClick={() => handleUnenroll(course.id)}
                      className="flex-1 text-center px-4 py-2 bg-red-600 text-white rounded-xl shadow hover:bg-red-700 transition"
                    >
                      🚪 Unenroll
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEnroll(course.id)}
                      className="flex-1 text-center px-4 py-2 bg-blue-600 text-white rounded-xl shadow hover:bg-blue-700 transition"
                    >
                      🎟 Enroll
                    </button>
                  ))}

                {user.role === 'teacher' && course.teacher.id === user.id && (
                  <>
                    <button
                      onClick={() => handleAddMaterialClick(course.id)}
                      className="flex-1 text-center px-4 py-2 bg-purple-600 text-white rounded-xl shadow hover:bg-purple-700 transition"
                    >
                      📂 Upload
                    </button>
                    <button
                      onClick={() => handleDelete(course.id)}
                      className="flex-1 text-center px-4 py-2 bg-red-600 text-white rounded-xl shadow hover:bg-red-700 transition"
                    >
                      🗑 Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-lg mt-8 text-center">
          No courses available.
        </p>
      )}

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  </div>
);

}

export default Courses;
